import get from 'get-value'
import React, { FC, MutableRefObject, ReactElement, useEffect, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import Loader from '../../React/Common/Components/Loader'
import { getFeaturesConfig } from '../../React/Common/API/GenericConfigAPI'
import { FeaturesConfigDTO } from '../../NonReact/Types/Types'
import { JSONResponse } from '../../NonReact/common/Types/Types'
import { redirectToServlet, ServletCmds } from '../../NonReact/constants/ServletCommands'
import { ENABLE_NEW_AGENT_LIST_PAGE } from '../Constants/FeatureFlags'
import { ReactState } from '../../React/Common/Types/Types'
import { buildLegacyAdminEntityListQueryFromUrl } from '../Utils/adminListLegacyRedirect'

const GateState = {
    loading: 'loading',
    react: 'react',
    legacy: 'legacy'
} as const

type GateState = (typeof GateState)[keyof typeof GateState]

type Props = {
    children: ReactElement
}

/**
 * Loads {@link ENABLE_NEW_AGENT_LIST_PAGE} from features config before rendering the routed
 * ACD Agents list. When disabled, redirects to the legacy servlet admin page.
 *
 * @param props - Child tree to render when the feature is enabled.
 * @returns Loader, nothing (after servlet redirect), or children.
 */
export const AgentListFeatureGate: FC<Props> = ({ children }: Props): ReactElement => {
    const [gate, setGate]: ReactState<GateState> = useState<GateState>(GateState.loading)
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    useEffect((): void => {
        getFeaturesConfig()
            .then((response: JSONResponse<FeaturesConfigDTO>): void => {
                if (!isMounted.current) return

                setGate(get(response ?? {}, ENABLE_NEW_AGENT_LIST_PAGE) ? GateState.react : GateState.legacy)
            })
            .catch((): void => {
                if (isMounted.current) setGate(GateState.legacy)
            })
    }, [])

    useEffect((): void => {
        if (gate === GateState.legacy) {
            redirectToServlet(ServletCmds.ADMIN_AGENTS, buildLegacyAdminEntityListQueryFromUrl(window.location.search))
        }
    }, [gate])

    if (gate === GateState.loading) {
        return (
            <div className='router-loader-container'>
                <Loader />
            </div>
        )
    }

    if (gate === GateState.legacy) return <></>

    return children
}
